import { useMemo } from "react";

import { AppButton } from "app/components/common";
import { useTransactionSender } from "app/components/transactions";
import { CreateRewardsTxnParams } from "@bridgesplit/abf-sdk";
import {
    Result,
    LOADING_ERROR,
    convertDateJsToUnix,
    uiAmountToLamports,
    uiPercentToBps,
    getUnixTs,
    multiplierFactorToPercent
} from "@bridgesplit/utils";
import { Column } from "@bridgesplit/ui";
import { getVaultStakeWeightDurations, useVaultCreateRewardsTransaction } from "@bridgesplit/abf-react";

import { ManagerAddRewardsProps } from "./ManagerAddRewardsDialog";
import { useManagerViewContext } from "../../ManagerViewContext";

export function ManagerCreateRewardsCta(props: ManagerAddRewardsProps) {
    const { form, vaultExpanded } = props;
    const createRewards = useCreateRewards(props);
    const now = getUnixTs();

    const ctaError = useMemo(() => {
        if (!vaultExpanded) return "Loading";
        if (!form.rewardToken) return "No reward token selected";
        if (!form.rewardAmount) return "No reward amount selected";
        if (!form.rewardStartTime) return "No reward start time selected";
        if (!form.rewardEndTime) return "No reward end time selected";
        if (form.rewardStartTime >= form.rewardEndTime) return "Reward start time must be before end time";

        const startTimeUnix = form.rewardStartTime ? convertDateJsToUnix(form.rewardStartTime) : undefined;
        const endTimeUnix = form.rewardEndTime ? convertDateJsToUnix(form.rewardEndTime) : undefined;

        if (startTimeUnix && startTimeUnix < now) return "Reward start time must be after current time";
        if (endTimeUnix && endTimeUnix < now) return "Reward end time must be after current time";
        if (!form.durationStakeWeights) return "No reward duration stake weights selected";
        if (form.durationStakeWeights.length !== getVaultStakeWeightDurations().length)
            return "Must set weights for all durations";
        if (form.durationStakeWeights.filter((weight) => weight === undefined).length > 0)
            return "All duration stakes must have weights";
        return undefined;
    }, [
        form.durationStakeWeights,
        form.rewardAmount,
        form.rewardEndTime,
        form.rewardStartTime,
        form.rewardToken,
        now,
        vaultExpanded
    ]);

    return (
        <Column spacing={1}>
            <AppButton
                asyncCta={{ onClickWithResult: createRewards }}
                isTransaction
                color={"secondary"}
                variant="contained"
                disabled={!!ctaError}
                sx={{ minWidth: "110px" }}
            >
                {ctaError ? ctaError : "Create Rewards"}
            </AppButton>
        </Column>
    );
}

function useCreateRewards({ form, vaultExpanded }: ManagerAddRewardsProps) {
    const createRewards = useVaultCreateRewardsTransaction();
    const { reset } = useManagerViewContext();
    const send = useTransactionSender();

    return async () => {
        const rewardStartTime = convertDateJsToUnix(form.rewardStartTime);
        const rewardEndTime = convertDateJsToUnix(form.rewardEndTime);
        if (
            !vaultExpanded ||
            !form.rewardToken ||
            !form.rewardStartTime ||
            !form.rewardEndTime ||
            !form.durationStakeWeights ||
            !form.rewardAmount ||
            !rewardStartTime ||
            !rewardEndTime ||
            rewardStartTime >= rewardEndTime
        ) {
            return Result.errFromMessage(LOADING_ERROR);
        }

        const amountToDeposit = uiAmountToLamports(form.rewardAmount, form.rewardToken.decimals);
        const durationStakeWeights = form.durationStakeWeights.map((weight) =>
            uiPercentToBps(multiplierFactorToPercent(weight), "cbps")
        );

        const params: CreateRewardsTxnParams = {
            vaultAddress: vaultExpanded.vault.address,
            amountToDeposit: amountToDeposit,
            rewardMint: form.rewardToken.mint,
            rewardStartTime: rewardStartTime,
            rewardEndTime: rewardEndTime,
            durationStakeWeights: durationStakeWeights
        };

        return await send(createRewards, params, { sendOptions: { onSuccess: reset } });
    };
}
